import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { FilterValue, MappedFilter } from 'src/app/products/services/filters-adapter.service';
import { ColorFilterComponent } from '../product-filters/color-filter/color-filter.component';
import { CommonFilterComponent } from '../product-filters/common-filter/common-filter.component';
import { SizeFilterComponent } from '../product-filters/size-filter/size-filter.component';
import { FilterCollapseComponent } from './filter-collapse/filter-collapse.component';
import { GlobalFiltersService } from './services/global-filters.service';

export interface FilterSelection {
  filter: FilterValue;
  type?: string;
  label?: string;
  id?: string;
}

enum AttributeType {
  Size = 'size',
  Color = 'color'
}

@Component({
  standalone: true,
  selector: 'base-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],

  imports: [CommonModule, FilterCollapseComponent, SizeFilterComponent, ColorFilterComponent, CommonFilterComponent]
})
export class FiltersComponent {
  private globalFiltersService = inject(GlobalFiltersService);

  IMAGE_URLS = IMAGE_URLS;

  AttributeType = AttributeType;

  filtersInput = input<MappedFilter[]>([]);
  
  // Internal filters signal to avoid mutating input directly
  private _filters = signal<MappedFilter[]>([]);
  readonly filters = this._filters.asReadonly();
  
  // Flag to prevent effect from overwriting when we update internally
  private _isInternalUpdate = false;

  // Computed properties
  readonly appliedFilters = computed(() => this.globalFiltersService.currentAppliedFilters());

  // Update output to use Angular 20 output() function
  close = output<void>();

  constructor() {
    // Sync filters input to internal signal, preserving checked state
    effect(() => {
      // Skip if we're updating internally to prevent overwriting
      if (this._isInternalUpdate) {
        return;
      }
      
      const inputFilters = this.filtersInput();
      if (inputFilters && inputFilters.length > 0) {
        const currentFilters = this._filters();
        
        // Merge new filters with existing checked state
        const mergedFilters = inputFilters.map(inputFilterGroup => {
          // Find matching filter group in current filters
          const currentFilterGroup = currentFilters.find(
            cf => cf.label === inputFilterGroup.label && cf.type === inputFilterGroup.type
          );
          
          return {
            ...inputFilterGroup,
            filters: inputFilterGroup.filters.map(inputFilter => {
              // Find matching filter in current filters to preserve checked state
              const currentFilter = currentFilterGroup?.filters.find(
                cf => cf.value === inputFilter.value && cf.id === inputFilter.id
              );
              
              // Preserve checked state if it exists, otherwise use input value
              return {
                ...inputFilter,
                checked: currentFilter?.checked ?? inputFilter.checked ?? false
              };
            })
          };
        });
        
        // Always update to preserve checked state, but only if we have current filters
        // or if the input is different from current (to avoid unnecessary updates)
        if (currentFilters.length === 0 || 
            currentFilters.length !== mergedFilters.length ||
            mergedFilters.some((mf, idx) => {
              const cf = currentFilters[idx];
              if (!cf || cf.label !== mf.label || cf.type !== mf.type) return true;
              if (mf.filters.length !== cf.filters.length) return true;
              // Check if any filter values changed (not checked state)
              return mf.filters.some((f, fIdx) => {
                const currentF = cf.filters[fIdx];
                return !currentF || currentF.value !== f.value || currentF.id !== f.id;
              });
            })) {
          this._filters.set(mergedFilters);
        }
      }
    });
  }

  onClose() {
    this.close.emit();
  }

  resetAllFilters() {
    // Create new array to avoid mutating signal directly
    const updatedFilter = this._filters().map(filterGroup => ({
      ...filterGroup,
      filters: filterGroup.filters.map(_filter => ({
        ..._filter,
        checked: false
      }))
    }));

    // Set flag to prevent effect from overwriting
    this._isInternalUpdate = true;
    
    // Update internal filters signal
    this._filters.set(updatedFilter);
    
    // Update service
    this.globalFiltersService.updateAggregations(updatedFilter);
    this.globalFiltersService.applyFilters([]);
    
    // Reset flag after a tick
    setTimeout(() => {
      this._isInternalUpdate = false;
    }, 0);
  }

  updateFilter(selectedFilter: FilterValue) {
    // will store checked filters
    const filtersApplied: FilterSelection[] = [];

    // Create new array to avoid mutating signal directly
    const updatedFilter = this._filters().map(filterGroup => {
      // select unselect filter checkboxes logic - create new objects
      const updatedFilters = filterGroup.filters.map(_filter => {
        if (_filter?.value === selectedFilter.value && _filter?.id === selectedFilter.id) {
          return {
            ..._filter,
            checked: !_filter?.checked
          };
        } else {
          return { ..._filter };
        }
      });

      // updated selection flags for filters
      // create array of checked filters to product layout
      updatedFilters.forEach(filterValue => {
        if (filterValue.checked) {
          filtersApplied.push({
            filter: filterValue,
            type: filterGroup.type,
            label: filterGroup?.label,
            id: filterValue?.id
          });
        }
      });

      // return selected and unselected filters with new reference
      return {
        ...filterGroup,
        filters: updatedFilters
      };
    });

    // Set flag to prevent effect from overwriting
    this._isInternalUpdate = true;
    
    // Update internal filters signal
    this._filters.set(updatedFilter);
    
    // update filters with checked and unchecked filters
    this.globalFiltersService.updateAggregations(updatedFilter);
    // emit checked filters to product layout through filterService
    this.globalFiltersService.applyFilters(filtersApplied);
    
    // Reset flag after a tick to allow effect to run for external updates
    setTimeout(() => {
      this._isInternalUpdate = false;
    }, 0);
  }

}


